/**
 * Video Material Component
 *
 * Renders video players (Cloudflare Stream or Mux) with analytics tracking.
 */

import { useTranslation } from 'react-i18next';
import {
  getLocalizedTitle,
  CloudflareStreamPlayer,
  MuxPlayer,
  useVideoUrl,
  type Material,
} from '@immopersian/common';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorMessage } from '../shared/ErrorMessage';
import { MaterialDescription } from '../shared/MaterialDescription';
import { useVideoTracking } from './useVideoTracking';

interface VideoMaterialProps {
  material: Material;
  sessionId?: string;
}

export function VideoMaterial({ material, sessionId }: VideoMaterialProps) {
  const { t, i18n } = useTranslation();

  // Fetch signed video URL from API
  const { data: videoData, isLoading, error } = useVideoUrl(material.id, material.type);

  // Setup video tracking
  const { handlePlay, handleProgress, handleEnded } = useVideoTracking(material, sessionId);

  if (isLoading) {
    return <LoadingSpinner message={t('courses.loadingVideo')} />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  // Render player based on provider
  const renderPlayer = () => {
    if (!videoData) {
      return (
        <div className="flex items-center justify-center h-full text-white">
          {t('courses.videoUnavailable')}
        </div>
      );
    }

    // Cloudflare Stream
    if (videoData.provider === 'cloudflare' && videoData.videoId) {
      return (
        <CloudflareStreamPlayer
          videoId={videoData.videoId}
          signedToken={videoData.signedToken}
          poster={(material as any).thumbnail_url}
          className="w-full h-full"
          preload="metadata"
          onPlay={handlePlay}
          onEnded={handleEnded}
          onProgress={handleProgress}
        />
      );
    }

    // Mux
    if (videoData.provider === 'mux' && videoData.playbackId) {
      return (
        <MuxPlayer
          playbackId={videoData.playbackId}
          signedToken={videoData.signedToken}
          poster={videoData.thumbnailUrl || (material as any).thumbnail_url}
          className="w-full h-full"
          onPlay={handlePlay}
          onEnded={handleEnded}
          onProgress={handleProgress}
        />
      );
    }

    return (
      <div className="flex items-center justify-center h-full text-white">
        {t('courses.videoUnavailable')}
      </div>
    );
  };

  return (
    <>
      <div className="aspect-video bg-black rounded-lg overflow-hidden" data-protected>
        {renderPlayer()}
      </div>
      <MaterialDescription material={material} />
    </>
  );
}
